import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import 'bootstrap/dist/css/bootstrap.min.css';
import { FaPlus, FaCloudUploadAlt, FaTimes, FaArrowLeft } from 'react-icons/fa';
import axios from 'axios';
import EnhancedRichTextEditor from './EnhancedRichTextEditor';
import { API_ENDPOINTS } from '../config/api';
import API_BASE_URL from '../config/api';

const PublicationForm = ({ editMode, onHide }) => {
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [customUrlManuallyEdited, setCustomUrlManuallyEdited] = useState(false);
  const [author, setAuthor] = useState('');
  const [publishedDate, setPublishedDate] = useState(new Date().toISOString().split('T')[0]);
  const [content, setContent] = useState('');
  const [publicationType, setPublicationType] = useState('');
  const [category, setCategory] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [publicationTypes, setPublicationTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [existingImagePath, setExistingImagePath] = useState(null);
  const [uploadedPdf, setUploadedPdf] = useState(null);
  const [existingPdfPath, setExistingPdfPath] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [pdfDragActive, setPdfDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const navigate = useNavigate();
  const { identifier } = useParams();
  const { token } = useAuth();
  
  // Set default values for props if not provided
  const isEditMode = editMode || false;
  const handleHide = onHide || (() => navigate('/dashboard'));

  // Helper function to construct full image URL
  const getFullImageUrl = (imagePath) => {
    if (!imagePath) return null;
    
    // If it's already a full URL, return as is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    
    // If it's a relative path starting with /uploads, construct full URL
    if (imagePath.startsWith('/uploads/')) {
      const fullUrl = `${API_BASE_URL}${imagePath}`;
      console.log('🔗 Constructed image URL:', {
        original: imagePath,
        baseUrl: API_BASE_URL,
        fullUrl: fullUrl
      });
      return fullUrl;
    }
    
    // If it's just a filename, construct full URL with uploads path
    const fullUrl = `${API_BASE_URL}/uploads/${imagePath}`;
    console.log('🔗 Constructed image URL:', {
      original: imagePath,
      baseUrl: API_BASE_URL,
      fullUrl: fullUrl
    });
    return fullUrl;
  };

  // Handle back button click
  const handleBackClick = () => {
    navigate('/dashboard/publications');
  };

  // Function to generate custom URL from title
  const generateCustomUrl = (title) => {
    if (!title) return '';
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .trim('-'); // Remove leading/trailing hyphens
  };

  // Auto-generate custom URL when title changes
  useEffect(() => {
    if (!isEditMode || !customUrlManuallyEdited) {
      setCustomUrl(generateCustomUrl(title));
    }
  }, [title, isEditMode, customUrlManuallyEdited]);

  // Image upload handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFile = (file) => {
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    const maxSize = 500 * 1024; // 500KB

    if (!allowedTypes.includes(file.type)) {
      setError('❌ Only PNG, JPEG, JPG, and SVG files are allowed.');
      return;
    }

    if (file.size > maxSize) {
      setError('❌ File size must be less than 500KB.');
      return;
    }

    setError(null);
    setUploadedImage(file);
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const removeImage = () => {
    setUploadedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // PDF upload handlers
  const handlePdfDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setPdfDragActive(true);
    } else if (e.type === "dragleave") {
      setPdfDragActive(false);
    }
  };

  const handlePdfDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPdfDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handlePdfFile(e.dataTransfer.files[0]);
    }
  };

  const handlePdfFile = (file) => {
    const allowedTypes = ['application/pdf'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!allowedTypes.includes(file.type)) {
      setError('❌ Only PDF files are allowed.');
      return;
    }

    if (file.size > maxSize) {
      setError('❌ PDF file size must be less than 5MB.');
      return;
    }

    setError(null);
    setUploadedPdf(file);
  };

  const handlePdfInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handlePdfFile(e.target.files[0]);
    }
  };

  const removePdf = () => {
    setUploadedPdf(null);
    if (pdfInputRef.current) {
      pdfInputRef.current.value = '';
    }
  };


  useEffect(() => {
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };

    axios.get(API_ENDPOINTS.PUBLICATION_TYPES_PUBLIC, { headers })
      .then(res => setPublicationTypes(res.data));
    axios.get(API_ENDPOINTS.CATEGORIES_PUBLIC, { headers })
      .then(res => setCategories(res.data));
    axios.get(API_ENDPOINTS.DOCUMENT_TYPES_PUBLIC, { headers })
      .then(res => setDocumentTypes(res.data));
  }, [token]);

  // Removed auto-selection of first items - dropdowns will default to empty

  useEffect(() => {
    if (isEditMode && identifier) {
      setLoading(true);
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      };

      axios.get(`${API_ENDPOINTS.PUBLICATIONS}/${identifier}`, { headers })
        .then((response) => {
          const data = response.data;
          setTitle(data.title || '');
          setSubtitle(data.subtitle || '');
          setCustomUrl(data.custom_url || '');
          setCustomUrlManuallyEdited(false); // Reset flag when loading existing data
          setAuthor(data.author || '');
          setPublishedDate(data.published_date ? new Date(data.published_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]);
          setContent(data.content || '');
          setPublicationType(data.publication_type_name || '');
          setCategory(data.category_name || '');
          setDocumentType(data.document_type_name || '');
          setExistingImagePath(data.image_path || null);
          setExistingPdfPath(data.file_path || null);
          console.log('📸 Publication data loaded:', {
            title: data.title,
            image_path: data.image_path,
            file_path: data.file_path,
            full_image_url: getFullImageUrl(data.image_path)
          });
          setLoading(false);
        })
        .catch((err) => {
          setError(err.response?.data?.error || err.message);
          setLoading(false);
        });
    }
  }, [isEditMode, identifier, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      // Find the IDs for the selected names
      const selectedPublicationType = publicationTypes.find(pt => pt.name === publicationType);
      const selectedCategory = categories.find(c => c.name === category);
      const selectedDocumentType = documentTypes.find(dt => dt.name === documentType);

      let imagePath = null;
      let filePath = null;
      
      // Upload image if one is selected
      if (uploadedImage) {
        const formData = new FormData();
        formData.append('image', uploadedImage);
        
        // Add publication type and custom URL for proper folder organization
        if (publicationType) {
          formData.append('publication_type_name', publicationType);
        }
        if (customUrl.trim()) {
          formData.append('custom_url', customUrl.trim());
        }
        
        console.log('📤 Uploading image with parameters:', {
          publication_type_name: publicationType,
          custom_url: customUrl.trim()
        });
        
        const uploadResponse = await axios.post(API_ENDPOINTS.UPLOAD_IMAGE, formData);
        
        imagePath = uploadResponse.data.imageUrl;
        console.log('✅ Image uploaded successfully:', imagePath);
      } else if (isEditMode && existingImagePath) {
        // Keep existing image if no new image is uploaded in edit mode
        imagePath = existingImagePath;
      }

      // Upload PDF if one is selected
      if (uploadedPdf) {
        const formData = new FormData();
        formData.append('pdf', uploadedPdf);
        
        // Add publication type and custom URL for proper folder organization
        if (publicationType) {
          formData.append('publication_type_name', publicationType);
        }
        if (customUrl.trim()) {
          formData.append('custom_url', customUrl.trim());
        }
        
        console.log('📤 Uploading PDF with parameters:', {
          publication_type_name: publicationType,
          custom_url: customUrl.trim()
        });
        
        const uploadResponse = await axios.post(API_ENDPOINTS.UPLOAD_PDF, formData);
        
        filePath = uploadResponse.data.pdfUrl;
        console.log('✅ PDF uploaded successfully:', filePath);
      } else if (isEditMode && existingPdfPath) {
        // Keep existing PDF if no new PDF is uploaded in edit mode
        filePath = existingPdfPath;
      }

      const payload = {
        title: title.trim(),
        subtitle: subtitle.trim(),
        custom_url: customUrl.trim(),
        author: author.trim(),
        published_date: publishedDate,
        content: content.trim(),
        publication_type_id: selectedPublicationType?.id || null,
        category_id: selectedCategory?.id || null,
        document_type_id: selectedDocumentType?.id || null,
        image_path: imagePath,
        file_path: filePath,
      };

      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      };

      if (isEditMode) {
        await axios.put(`${API_ENDPOINTS.PUBLICATIONS}/${identifier}`, payload, { headers });
      } else {
        await axios.post(API_ENDPOINTS.PUBLICATIONS, payload, { headers });
      }
      navigate('/dashboard/publications');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setTitle('');
    setSubtitle('');
    setCustomUrl('');
    setCustomUrlManuallyEdited(false);
    setAuthor('');
    setPublishedDate(new Date().toISOString().split('T')[0]);
    setContent('');
    setPublicationType('');
    setCategory('');
    setDocumentType('');
    setUploadedImage(null);
    setExistingImagePath(null);
    setUploadedPdf(null);
    setExistingPdfPath(null);
    setError(null);
  };

  if (isEditMode && loading) return <div className="loading">🔄 Loading publication for editing...</div>;

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="fw-bold mb-0">{isEditMode ? 'Edit Publication' : 'Create New Publication'}</h2>
        <button 
          type="button" 
          className="btn btn-outline-secondary d-flex align-items-center gap-2"
          onClick={handleBackClick}
          style={{ 
            borderRadius: '8px',
            padding: '8px 16px',
            fontWeight: '500',
            transition: 'all 0.2s ease'
          }}
        >
          <FaArrowLeft size={14} />
          Back to List
        </button>
      </div>
      <div className="bg-white rounded shadow-sm p-4">
        <div className="mb-4">
          <h4 className="fw-bold mb-1"><span role="img" aria-label="builder">🛠️</span> Publication Builder</h4>
          {!isEditMode && <div className="text-muted">Create a new publication by filling out the form below</div>}
        </div>
        <form onSubmit={handleSubmit}>
          <div className="row mb-3">
            <div className="col-md-6 mb-3 mb-md-0">
              <label className="form-label fw-semibold">
                Publication Title <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <input className="form-control" type="text" value={title} onChange={e => {
                setTitle(e.target.value);
                // If user is editing title and custom URL was manually edited, 
                // offer to regenerate it
                if (customUrlManuallyEdited && isEditMode) {
                  setCustomUrlManuallyEdited(false);
                }
              }} required placeholder="Enter publication title" />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">
                Author <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <input className="form-control" type="text" value={author} onChange={e => setAuthor(e.target.value)} required placeholder="Enter author name" />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-12">
              <label className="form-label fw-semibold">
                Subtitle
              </label>
              <input 
                className="form-control" 
                type="text" 
                value={subtitle} 
                onChange={e => setSubtitle(e.target.value)} 
                placeholder="Enter publication subtitle (optional)" 
              />
              <div className="form-text text-muted">
                A brief description or subtitle for the publication
              </div>
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-12">
              <label className="form-label fw-semibold">
                Custom URL
              </label>
              <div className="input-group">
                <input 
                  className="form-control" 
                  type="text" 
                  value={customUrl} 
                  onChange={e => {
                    setCustomUrl(e.target.value);
                    setCustomUrlManuallyEdited(true);
                  }} 
                  placeholder="Enter custom URL (auto-generated from title)" 
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setCustomUrl(generateCustomUrl(title));
                    setCustomUrlManuallyEdited(false);
                  }}
                  title="Regenerate from title"
                >
                  🔄
                </button>
              </div>
              <div className="form-text text-muted">
                This will be used in the URL: /publications/[your-custom-url]
              </div>
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label fw-semibold">
                Publication Date
              </label>
              <input 
                className="form-control" 
                type="date" 
                value={publishedDate} 
                onChange={e => setPublishedDate(e.target.value)} 
                disabled={true}
                style={{
                  backgroundColor: '#f8f9fa',
                  cursor: 'not-allowed'
                }}
              />
              <div className="form-text text-muted">
                Publication date is automatically set and cannot be modified
              </div>
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-4 mb-3 mb-md-0">
              <label className="form-label fw-semibold">
                Publication Type <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <select className="form-select" value={publicationType} onChange={e => setPublicationType(e.target.value)} required>
                <option value="">Select type</option>
                {publicationTypes.map(type => (
                  <option key={type.id} value={type.name}>{type.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-4 mb-3 mb-md-0">
              <label className="form-label fw-semibold">
                Category <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <select className="form-select" value={category} onChange={e => setCategory(e.target.value)} required>
                <option value="">Select category</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label fw-semibold">
                Document Type <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <select className="form-select" value={documentType} onChange={e => setDocumentType(e.target.value)} required>
                <option value="">Select document type</option>
                {documentTypes.map(doc => (
                  <option key={doc.id} value={doc.name}>{doc.name}</option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Image Upload Section */}
          <div className="row mb-3">
            <div className="col-12">
              <label className="form-label fw-semibold">
                Publication Image
              </label>
              <div 
                className={`border-2 border-dashed rounded p-4 text-center ${dragActive ? 'border-primary bg-light' : 'border-secondary'}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                style={{ 
                  borderStyle: 'dashed',
                  borderWidth: '2px',
                  minHeight: '150px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                {uploadedImage ? (
                  <div className="w-100">
                    <div className="position-relative d-inline-block">
                      <img 
                        src={URL.createObjectURL(uploadedImage)} 
                        alt="Preview" 
                        className="img-fluid rounded"
                        style={{ maxHeight: '200px', maxWidth: '100%' }}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeImage();
                        }}
                        style={{ zIndex: 10 }}
                      >
                        <FaTimes />
                      </button>
                    </div>
                    <div className="mt-2 text-muted">
                      <small>{uploadedImage.name}</small>
                    </div>
                  </div>
                ) : existingImagePath ? (
                  <div className="w-100">
                    <div className="position-relative d-inline-block">
                      <img 
                        src={getFullImageUrl(existingImagePath)} 
                        alt="Current Publication Image" 
                        className="img-fluid rounded"
                        style={{ maxHeight: '200px', maxWidth: '100%' }}
                        onError={(e) => {
                          const fullUrl = getFullImageUrl(existingImagePath);
                          console.log('❌ Image failed to load:', {
                            originalPath: existingImagePath,
                            fullUrl: fullUrl,
                            error: e.target.error || 'Unknown error'
                          });
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                        onLoad={() => {
                          const fullUrl = getFullImageUrl(existingImagePath);
                          console.log('✅ Image loaded successfully:', {
                            originalPath: existingImagePath,
                            fullUrl: fullUrl
                          });
                        }}
                      />
                      <div 
                        className="text-center text-muted"
                        style={{ display: 'none', padding: '20px' }}
                      >
                        <FaCloudUploadAlt size={48} className="mb-3" />
                        <p>Image not available</p>
                        <small className="text-muted">Path: {existingImagePath}</small>
                        <br />
                        <small className="text-muted">Full URL: {getFullImageUrl(existingImagePath)}</small>
                        <br />
                        <small className="text-muted">Backend URL: {API_BASE_URL}</small>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExistingImagePath(null);
                        }}
                        style={{ zIndex: 10 }}
                      >
                        <FaTimes />
                      </button>
                    </div>
                    <div className="mt-2 text-muted">
                      <small>Current publication image</small>
                      <br />
                      <small className="text-muted">Path: {existingImagePath}</small>
                      <br />
                      <small className="text-muted">Full URL: {getFullImageUrl(existingImagePath)}</small>
                      <br />
                      <small className="text-muted">Backend: {API_BASE_URL}</small>
                    </div>
                  </div>
                ) : (
                  <>
                    <FaCloudUploadAlt size={48} className="text-muted mb-3" />
                    <h5 className="mb-2">Drag & Drop Image Here</h5>
                    <p className="text-muted mb-2">or click to browse files</p>
                    <p className="text-muted small">
                      Allowed formats: PNG, JPEG, JPG, SVG (Max size: 500KB)
                    </p>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".png,.jpeg,.jpg,.svg"
                  onChange={handleFileInput}
                  style={{ display: 'none' }}
                />
              </div>
            </div>
          </div>

          {/* PDF Upload Section */}
          <div className="mb-3">
            <label className="form-label fw-semibold">PDF Document (Optional)</label>
            <div 
              className={`border-2 border-dashed rounded p-4 text-center ${pdfDragActive ? 'border-primary bg-light' : 'border-secondary'}`}
              onDragEnter={handlePdfDrag}
              onDragLeave={handlePdfDrag}
              onDragOver={handlePdfDrag}
              onDrop={handlePdfDrop}
              onClick={() => pdfInputRef.current?.click()}
              style={{ cursor: 'pointer' }}
            >
              {uploadedPdf ? (
                <div>
                  <div className="d-flex align-items-center justify-content-center mb-2">
                    <FaCloudUploadAlt size={24} className="text-success me-2" />
                    <span className="fw-semibold text-success">{uploadedPdf.name}</span>
                    <button 
                      type="button" 
                      className="btn btn-sm btn-outline-danger ms-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        removePdf();
                      }}
                    >
                      <FaTimes />
                    </button>
                  </div>
                  <small className="text-muted">
                    Size: {(uploadedPdf.size / 1024 / 1024).toFixed(2)} MB
                  </small>
                </div>
              ) : existingPdfPath ? (
                <div>
                  <div className="d-flex align-items-center justify-content-center mb-2">
                    <FaCloudUploadAlt size={24} className="text-primary me-2" />
                    <span className="fw-semibold text-primary">Existing PDF: {existingPdfPath.split('/').pop()}</span>
                    <button 
                      type="button" 
                      className="btn btn-sm btn-outline-danger ms-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExistingPdfPath(null);
                      }}
                    >
                      <FaTimes />
                    </button>
                  </div>
                  <small className="text-muted">Click to replace with new PDF</small>
                </div>
              ) : (
                <>
                  <FaCloudUploadAlt size={48} className="text-muted mb-3" />
                  <h5 className="mb-2">Drag & Drop PDF Here</h5>
                  <p className="text-muted mb-2">or click to browse files</p>
                  <p className="text-muted small">
                    Allowed format: PDF (Max size: 5MB)
                  </p>
                </>
              )}
              <input
                ref={pdfInputRef}
                type="file"
                accept=".pdf"
                onChange={handlePdfInput}
                style={{ display: 'none' }}
              />
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label fw-semibold">Content</label>
            <EnhancedRichTextEditor
              value={content}
              onChange={setContent}
              placeholder="Enter publication content..."
              editorType="quill"
              height="300px"
            />
          </div>
          {error && <div className="alert alert-danger">❌ {error}</div>}
          <div className="d-flex justify-content-end gap-2">
            <button type="button" className="btn btn-outline-secondary" onClick={handleClear}>Clear Form</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? '🔄 Saving...' : (isEditMode ? '💾 Update Publication' : 'Create Publication')}
            </button>
          </div>
        </form>
      </div>


    </div>
  );
};

export default PublicationForm; 